import type { GraphqlMappedError } from "@/services/graphqlErrorService";
import type { IFolder } from "@/types/folder";
import type { FormikHelpers } from "formik";
import type { ReactNode } from "react";

import { useReactiveVar } from "@apollo/client/react";
import { useEffect, useState } from "react";
import {
  BoxArrowUpRight,
  Download,
  FolderPlus,
  FolderSymlink,
  Globe,
  Pencil,
  PersonCheck,
  Share,
  Sliders2,
  Trash,
} from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  CreateSubfolderModalLazy,
  DeleteFolderModalLazy,
  DownloadFolderModalLazy,
  MoveSubfolderModalLazy,
  RenameFolderModalLazy,
} from "@/components/folderModals";
import RenderOnce from "@/components/ui/renderOnce";
import { IS_ME_LINK } from "@/constants/app";
import { FOLDER_NOT_UNIQUE_NAME_ERROR_KEY } from "@/constants/folders";
import { CREATE_SUBFOLDER_FIELDS } from "@/constants/formFields/createSubfolderModalFields";
import { RENAME_FOLDER_MODAL_FIELDS } from "@/constants/formFields/renameFolderModalFields";
import { ROUTES } from "@/constants/routes";
import { currentFolderVar } from "@/graphql/globalVar/state";
import { GqlFolderReportStatus } from "@/graphql/schema/types";
import useCreateSubfolder from "@/hooks/folders/useCreateSubfolder";
import useDeleteFolder from "@/hooks/folders/useDeleteFolder";
import useDownloadFolderQrZip from "@/hooks/folders/useDownloadFolderQrZip";
import useFolderZipReport from "@/hooks/folders/useFolderZipReport";
import useGenerateOrGetFolderReport from "@/hooks/folders/useGenerateOrGetFolderReport";
import useGetUserFolders from "@/hooks/folders/useGetUserFolders";
import useMarkFolderReportDownloaded from "@/hooks/folders/useMarkFolderReportDownloaded";
import useMoveSubfolder from "@/hooks/folders/useMoveSubfolder";
import useRenameFolder from "@/hooks/folders/useRenameFolder";
import useUndownloadedFolderReports from "@/hooks/folders/useUndownloadedFolderReports";
import useLocalizedHref from "@/hooks/useLocalizedHref";
import { useAuthService } from "@/services/authService";
import { graphqlErrorService } from "@/services/graphqlErrorService";
import { notificationService } from "@/services/notificationService";
import { canEditFolder, isFolderOwnerContext } from "@/utils/folderAccess";
import {
  isSameNormalizedFolderName,
  normalizeFolderName,
} from "@/utils/normalizeFolderName";

function hasFolderWithName(
  folders: IFolder[],
  name: string,
  ignoredFolderId?: number
) {
  return folders.some(
    (folder) =>
      folder.id !== ignoredFolderId &&
      isSameNormalizedFolderName(folder.name, name)
  );
}

function findParentFolder(folders: IFolder[], parentFolderId: number) {
  return folders.find((folder) => folder.id === parentFolderId) ?? null;
}

export type TFolderModal =
  | "createFolderModal"
  | "renameModal"
  | "createSubfolderModal"
  | "downloadModal"
  | "deleteModal"
  | "moveSubfolderModal"
  | null;

export interface FolderActionItem {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

export interface UseFolderActionsResult {
  items: FolderActionItem[];
  deleteItem: FolderActionItem;
  modals: ReactNode;
}

export function useFolderActions(
  folder: IFolder,
  placement: "sidebar" | "page" | "sheet"
): UseFolderActionsResult {
  const { t } = useTranslation();
  const localizedHref = useLocalizedHref();
  const navigate = useNavigate();
  const [activeModal, setActiveModal] = useState<TFolderModal>(null);
  const { id: currentUserId } = useAuthService();

  const currentFolder = useReactiveVar(currentFolderVar);
  const { data: userFolders, refetch: refetchFolders } = useGetUserFolders();
  const [searchParams, setSearchParams] = useSearchParams();

  // Shared VIEW: read-only folder actions. Shared EDIT: rename/subfolder/delete/settings.
  // Owner-only (domain, change owner) stay gated by isShared.
  // Role may be inherited from an ancestor shared folder.
  const editDisabled = !canEditFolder(folder, currentUserId, userFolders);
  const ownerOnlyDisabled = !isFolderOwnerContext(folder);
  const isFolder = folder.parentFolderId === null;
  const isSubfolder = folder.parentFolderId !== null;

  const { deleteFolder, loading: deleteLoading } = useDeleteFolder({
    onCompleted: async () => {
      await refetchFolders();
      if (currentFolder?.id === folder.id) {
        currentFolderVar(null);
        const params = new URLSearchParams(searchParams);
        params.delete("folder");
        params.set("page", "1");
        setSearchParams(params);
      }
    },
  });

  const { createSubfolder, loading: createSubfolderLoading } =
    useCreateSubfolder({
      onCompleted: () => {
        setActiveModal(null);
        refetchFolders();
      },
    });

  const { renameFolder, loading: renameFolderLoading } = useRenameFolder({
    onCompleted: async () => {
      await refetchFolders();
      setActiveModal(null);
    },
  });

  const { moveSubfolder, loading: moveSubfolderLoading } = useMoveSubfolder({
    onCompleted: () => {
      setActiveModal(null);
      refetchFolders();
    },
  });

  const handleDownloadFolderQrZipError = (e: GraphqlMappedError) => {
    notificationService.danger({ msg: e.errorTranslation });
  };

  const { downloadFolderQrZip, loading: downloadFolderQrZipLoading } =
    useDownloadFolderQrZip({
      onError: handleDownloadFolderQrZipError,
    });
  const { fetchFolderZipReport, folderZipReport } = useFolderZipReport();
  const [isCheckingExistingReport, setIsCheckingExistingReport] =
    useState(false);
  const { refreshUndownloadedFolderReports } = useUndownloadedFolderReports();

  useEffect(() => {
    if (activeModal === "downloadModal") {
      setIsCheckingExistingReport(true);
      fetchFolderZipReport(folder.id).finally(() =>
        setIsCheckingExistingReport(false)
      );
      refreshUndownloadedFolderReports();
    }
  }, [
    activeModal,
    folder.id,
    fetchFolderZipReport,
    refreshUndownloadedFolderReports,
  ]);

  const handleMarkFolderReportDownloadedError = (e: GraphqlMappedError) => {
    notificationService.danger({ msg: e.errorTranslation });
  };

  const handleGenerateOrGetFolderReportError = (e: GraphqlMappedError) => {
    notificationService.danger({ msg: e.errorTranslation });
  };

  const { generateOrGetFolderReport } = useGenerateOrGetFolderReport({
    onError: handleGenerateOrGetFolderReportError,
  });
  const { markFolderReportDownloaded } = useMarkFolderReportDownloaded({
    onError: handleMarkFolderReportDownloadedError,
  });

  const onDeleteFolder = async () => {
    try {
      await deleteFolder({ id: folder.id });
    } catch (error) {
      const { errorTranslation } = graphqlErrorService(error);
      notificationService.danger({ msg: errorTranslation });
    }
  };

  const onCreateSubfolder = async (
    values: { [CREATE_SUBFOLDER_FIELDS.NAME]: string },
    { setErrors }: FormikHelpers<{ [CREATE_SUBFOLDER_FIELDS.NAME]: string }>
  ) => {
    const name = values[CREATE_SUBFOLDER_FIELDS.NAME];
    const duplicatedError = t(`errors:${FOLDER_NOT_UNIQUE_NAME_ERROR_KEY}`);

    if (hasFolderWithName(folder.subfolders ?? [], name)) {
      setErrors({ [CREATE_SUBFOLDER_FIELDS.NAME]: duplicatedError });

      return;
    }

    try {
      await createSubfolder({
        parentId: folder.id,
        name,
      });
    } catch (error) {
      const { errorFields, errorKey, errorTranslation } =
        graphqlErrorService(error);
      if (errorKey === FOLDER_NOT_UNIQUE_NAME_ERROR_KEY) {
        setErrors({
          [CREATE_SUBFOLDER_FIELDS.NAME]: duplicatedError,
        });

        return;
      }

      if (Object.keys(errorFields).length) setErrors(errorFields);
      else notificationService.danger({ msg: errorTranslation });
    }
  };

  const onRenameFolder = async (
    values: { [RENAME_FOLDER_MODAL_FIELDS.NAME]: string },
    { setErrors }: FormikHelpers<{ [RENAME_FOLDER_MODAL_FIELDS.NAME]: string }>
  ) => {
    const name = normalizeFolderName(values[RENAME_FOLDER_MODAL_FIELDS.NAME]);
    const duplicatedError = t(`errors:${FOLDER_NOT_UNIQUE_NAME_ERROR_KEY}`);
    const parentFolder = folder.parentFolderId
      ? findParentFolder(userFolders, folder.parentFolderId)
      : null;
    const siblingFolders = !folder.parentFolderId
      ? userFolders
      : (parentFolder?.subfolders ?? []);

    if (hasFolderWithName(siblingFolders, name, folder.id)) {
      setErrors({ [RENAME_FOLDER_MODAL_FIELDS.NAME]: duplicatedError });

      return;
    }

    try {
      await renameFolder({
        id: folder.id,
        name,
      });
      if (currentFolder?.id === folder.id) {
        currentFolderVar({ ...currentFolder, name });
      }
    } catch (error) {
      const { errorFields, errorKey, errorTranslation } =
        graphqlErrorService(error);
      if (errorKey === FOLDER_NOT_UNIQUE_NAME_ERROR_KEY) {
        setErrors({
          [RENAME_FOLDER_MODAL_FIELDS.NAME]: duplicatedError,
        });

        return;
      }

      if (Object.keys(errorFields).length) setErrors(errorFields);
      else notificationService.danger({ msg: errorTranslation });
    }
  };

  const onMoveSubfolder = async (
    values: { parentId: string },
    { setErrors }: FormikHelpers<{ parentId: string }>
  ) => {
    const parentId = Number(values.parentId);
    const duplicatedError = t(`errors:${FOLDER_NOT_UNIQUE_NAME_ERROR_KEY}`);
    const targetParentFolder = findParentFolder(userFolders, parentId);

    if (
      targetParentFolder &&
      hasFolderWithName(
        targetParentFolder.subfolders ?? [],
        folder.name,
        folder.id
      )
    ) {
      setErrors({ parentId: duplicatedError });

      return;
    }

    try {
      await moveSubfolder({
        parentId,
        childrenId: folder.id,
      });
    } catch (error) {
      const { errorFields, errorKey, errorTranslation } =
        graphqlErrorService(error);
      if (errorKey === FOLDER_NOT_UNIQUE_NAME_ERROR_KEY) {
        setErrors({ parentId: duplicatedError });

        return;
      }

      if (Object.keys(errorFields).length) setErrors(errorFields);
      else notificationService.danger({ msg: errorTranslation });
    }
  };

  const onDownloadFolderQrZip = async (values: {
    folderId: number;
    extension: string;
    size: number;
  }) => {
    try {
      const result = await downloadFolderQrZip({
        folderId: Number(values.folderId),
        extension: values.extension,
        size: Number(values.size),
      });

      setActiveModal(null);

      if (result?.url) {
        await markFolderReportDownloaded(Number(values.folderId));
        refreshUndownloadedFolderReports();
        window.location.href = result.url;
      } else {
        if (result?.status === GqlFolderReportStatus.Pending) {
          notificationService.info({
            msg: t(
              "components.folderModals.downloadFolderModal.preparingDownload"
            ),
          });
        }
        refreshUndownloadedFolderReports();
      }
    } catch (error) {
      const { errorTranslation } = graphqlErrorService(error);
      notificationService.danger({ msg: errorTranslation });
    }
  };

  const onDownloadExistingFolderQrZip = async () => {
    if (folderZipReport?.url) {
      await markFolderReportDownloaded(folder.id);
      refreshUndownloadedFolderReports();
      window.location.href = folderZipReport.url;
    }
    setActiveModal(null);
  };

  const onGenerateOrGetFolderReport = async (folderId: number) => {
    try {
      const result = await generateOrGetFolderReport(folderId);
      if (result?.downloadUrl) window.location.href = result.downloadUrl;
    } catch (error) {
      const { errorTranslation } = graphqlErrorService(error);
      notificationService.danger({ msg: errorTranslation });
    }
  };

  const items: FolderActionItem[] = (
    [
      (placement === "sidebar" || placement === "sheet") && {
        icon: <Pencil size={18} className="flex-shrink-0" />,
        label: t("components.folderDropdown.folder.rename"),
        onClick: () => setActiveModal("renameModal"),
        disabled: editDisabled,
      },
      isFolder && {
        icon: <FolderPlus size={18} className="flex-shrink-0" />,
        label: t("components.folderDropdown.folder.createSubfolder"),
        onClick: () => setActiveModal("createSubfolderModal"),
        disabled: editDisabled,
      },
      isSubfolder && {
        icon: <FolderSymlink size={18} className="flex-shrink-0" />,
        label: t("components.folderDropdown.subfolder.moveSubfolder"),
        onClick: () => setActiveModal("moveSubfolderModal"),
      },
      {
        icon: <Sliders2 size={18} className="flex-shrink-0" />,
        label: t("components.folderDropdown.folder.settings"),
        onClick: () =>
          navigate(`${localizedHref(ROUTES.FOLDER_SETTINGS)}?id=${folder.id}`),
        disabled: editDisabled,
      },
      !IS_ME_LINK && {
        icon: <Share size={18} className="flex-shrink-0" />,
        label: isFolder
          ? t("components.folderDropdown.folder.shareFolderAccess")
          : t("components.folderDropdown.subfolder.shareSubfolderAccess"),
        onClick: () =>
          navigate(
            `${localizedHref(ROUTES.SHARE_FOLDER_ACCESS)}?folderId=${folder.id}`
          ),
      },
      isFolder && {
        icon: <Globe size={18} className="flex-shrink-0" />,
        label: t("components.folderDropdown.folder.addDomain"),
        onClick: () =>
          navigate(localizedHref(ROUTES.DOMAIN), { state: { activeTab: 1 } }),
        disabled: ownerOnlyDisabled,
      },
      isFolder &&
        !IS_ME_LINK && {
          icon: <PersonCheck size={18} className="flex-shrink-0" />,
          label: t("components.folderDropdown.folder.changeFolderOwner"),
          onClick: () =>
            navigate(
              `${localizedHref(ROUTES.FOLDER_CHANGE_OWNER)}?folderId=${folder.id}`
            ),
          disabled: ownerOnlyDisabled,
        },
      {
        icon: <Download size={18} className="flex-shrink-0" />,
        label: isFolder
          ? t("components.folderDropdown.folder.downloadFolder")
          : t("components.folderDropdown.subfolder.downloadSubfolder"),
        onClick: () => setActiveModal("downloadModal"),
      },
      {
        icon: <BoxArrowUpRight size={18} className="flex-shrink-0" />,
        label: t("components.folderDropdown.folder.exportXLSX"),
        onClick: () => onGenerateOrGetFolderReport(folder.id),
      },
    ] as (FolderActionItem | false)[]
  ).filter(Boolean) as FolderActionItem[];

  const deleteItem: FolderActionItem = {
    icon: <Trash size={18} className="flex-shrink-0" />,
    label: isFolder
      ? t("components.folderDropdown.folder.deleteFolder")
      : t("components.folderDropdown.subfolder.deleteSubfolder"),
    onClick: () => setActiveModal("deleteModal"),
    disabled: editDisabled,
  };

  const modals = (
    <>
      <RenderOnce when={activeModal === "renameModal"}>
        <RenameFolderModalLazy
          show={activeModal === "renameModal"}
          folder={folder}
          handleClose={() => setActiveModal(null)}
          onAction={onRenameFolder}
          isActionLoading={renameFolderLoading}
        />
      </RenderOnce>
      <RenderOnce when={activeModal === "createSubfolderModal"}>
        <CreateSubfolderModalLazy
          show={activeModal === "createSubfolderModal"}
          handleClose={() => setActiveModal(null)}
          onAction={onCreateSubfolder}
          isActionLoading={createSubfolderLoading}
        />
      </RenderOnce>
      <RenderOnce when={activeModal === "downloadModal"}>
        <DownloadFolderModalLazy
          show={activeModal === "downloadModal"}
          folder={folder}
          handleClose={() => setActiveModal(null)}
          onAction={onDownloadFolderQrZip}
          isActionLoading={downloadFolderQrZipLoading}
          existingReport={folderZipReport}
          onDownloadExisting={onDownloadExistingFolderQrZip}
          isCheckingExistingReport={isCheckingExistingReport}
        />
      </RenderOnce>
      <RenderOnce when={activeModal === "deleteModal"}>
        <DeleteFolderModalLazy
          show={activeModal === "deleteModal"}
          folder={folder}
          handleClose={() => setActiveModal(null)}
          onAction={onDeleteFolder}
          isActionLoading={deleteLoading}
        />
      </RenderOnce>
      <RenderOnce when={activeModal === "moveSubfolderModal"}>
        <MoveSubfolderModalLazy
          show={activeModal === "moveSubfolderModal"}
          folder={folder}
          handleClose={() => setActiveModal(null)}
          onAction={onMoveSubfolder}
          isActionLoading={moveSubfolderLoading}
        />
      </RenderOnce>
    </>
  );

  return { items, deleteItem, modals };
}
